import {
  Compass,
  Eye,
  FileClock,
  Globe2,
  Languages,
  MousePointerClick,
  PlugZap,
  ScanSearch,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import {
  Band,
  CtaArrow,
  ctaClasses,
  INLINE_LINK,
  Lead,
  Medallion,
  Panel,
  SectionTitle,
  StatGrid,
} from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal, SectionHeader } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * `/about` — the idea (Agentic Experience), the product (the Experience
 * Agent), the rules it is built under, and where it honestly stands.
 *
 * ## What this page argues
 *
 * The page is the category manifesto, not a company scrapbook. Its arc: the
 * industry's answer to overloaded software was UX — help people FIND things —
 * and AI makes a successor discipline possible, where the product carries the
 * request out itself. The site's locked positioning names that discipline
 * Agentic Experience (AX) and the product the Experience Agent; this is the
 * one page that says so in full sentences rather than a hero line.
 *
 * ## What this page deliberately does NOT carry
 *
 * No team photographs, no headcount, no funding history, no founding date, no
 * office location. None of those facts is published anywhere else on this
 * site, and an About page is exactly where an unverified one would be invented
 * and then quoted back at us. `quality-bar.md` forbids example data in shipped
 * product code; the same rule applies with more force to a page whose subject
 * is who we are. Everything the page DOES claim is checkable against another
 * published surface: the discovery figures mirror /benchmarks, the commitments
 * mirror the product's enforced gates, and the compliance status mirrors the
 * Trust Center's own wording ("on the roadmap, not yet in hand" — never a
 * report claimed before it exists).
 *
 * ## Why the commitments are phrased as "never"
 *
 * "It never acts without asking" is a limitation on the product, not a feature
 * of it. Framing that section as things the agent will NEVER do is the honest
 * shape for a company asking to be allowed to act inside somebody else's
 * software, and it is the section a security reviewer reads first.
 */
interface IconCard {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly hue: MedallionHue;
}

/** The four halves of the Experience Agent, in the order a buyer meets them. */
const BUILD_CARDS: readonly IconCard[] = [
  { id: 'reads', icon: ScanSearch, hue: 'cyan' },
  { id: 'acts', icon: MousePointerClick, hue: 'blue' },
  { id: 'follows', icon: PlugZap, hue: 'violet' },
  { id: 'generates', icon: Sparkles, hue: 'purple' },
];

/** The five designed-in limits. Each is observable from a customer's own audit trail. */
const PRINCIPLES: readonly IconCard[] = [
  { id: 'permission', icon: Eye, hue: 'cyan' },
  { id: 'identity', icon: ShieldCheck, hue: 'blue' },
  { id: 'residency', icon: Globe2, hue: 'ok' },
  { id: 'record', icon: FileClock, hue: 'violet' },
  { id: 'language', icon: Languages, hue: 'purple' },
];

/** Checkable figures only — each one is published elsewhere on this site. */
const STATS = ['install', 'frameworks', 'languages', 'audit'] as const;

const STATE_ROWS = ['live', 'ahead', 'reach'] as const;

export function About(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('about.meta.title'),
    description: t('about.meta.description'),
    path: '/about',
  });

  return (
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        eyebrow={t('about.eyebrow')}
        title={t('about.title')}
        description={t('about.lead')}
      />

      {/* ── The idea: after UX comes AX ────────────────────────────────── */}
      <Band as="section" tone="page" space="lg" aria-labelledby="idea-heading">
        <Container width="prose">
          <SectionHeader id="idea-heading" align="start" eyebrow={t('about.idea.eyebrow')}>
            {t('about.idea.heading')}
          </SectionHeader>
          <div className="mt-7 flex flex-col gap-5 text-[1.05rem] leading-[1.72] text-[color:var(--bd-body)]">
            <p>{t('about.idea.body.1')}</p>
            <p>{t('about.idea.body.2')}</p>
            <p>{t('about.idea.body.3')}</p>
          </div>
          <div className="mt-10">
            <StatGrid
              stats={STATS.map((id) => ({
                value: t(`about.stats.${id}.value`),
                label: t(`about.stats.${id}.label`),
                accent: id === 'install',
              }))}
            />
          </div>
        </Container>
      </Band>

      {/* ── What we build: the Experience Agent ────────────────────────── */}
      <Band as="section" tone="sunk" space="lg" aria-labelledby="build-heading">
        <SectionHeader
          id="build-heading"
          eyebrow={t('about.build.eyebrow')}
          sub={t('about.build.sub')}
        >
          {t('about.build.heading')}
        </SectionHeader>

        <ul className="mt-11 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {BUILD_CARDS.map((card, i) => (
            <Reveal as="li" key={card.id} delayMs={i * 70}>
              <Panel className="flex h-full flex-col gap-3 p-6">
                <Medallion icon={card.icon} hue={card.hue} size="sm" />
                <h3 className="text-[1.05rem] font-semibold leading-[1.22] tracking-[-0.02em] text-[color:var(--bd-ink)]">
                  {t(`about.build.${card.id}.title`)}
                </h3>
                <p className="text-[0.93rem] leading-[1.62] text-[color:var(--bd-body)]">
                  {t(`about.build.${card.id}.body`)}
                </p>
              </Panel>
            </Reveal>
          ))}
        </ul>
      </Band>

      {/* ── The rules: five nevers ─────────────────────────────────────── */}
      <Band as="section" tone="band" space="lg" ambient aria-labelledby="principles-heading">
        <SectionHeader
          id="principles-heading"
          eyebrow={t('about.principles.eyebrow')}
          sub={t('about.principles.sub')}
          onDark
        >
          {t('about.principles.heading')}
        </SectionHeader>

        <ul className="mt-11 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRINCIPLES.map((principle, i) => (
            <Reveal as="li" key={principle.id} delayMs={i * 70}>
              <Panel onDark className="flex h-full flex-col gap-3 p-6">
                <Medallion icon={principle.icon} hue={principle.hue} onDark size="sm" />
                <h3 className="text-[1.05rem] font-semibold leading-[1.22] tracking-[-0.02em] text-white">
                  {t(`about.principle.${principle.id}.title`)}
                </h3>
                <p className="text-[0.93rem] leading-[1.62] text-[color:var(--bd-band-body)]">
                  {t(`about.principle.${principle.id}.body`)}
                </p>
              </Panel>
            </Reveal>
          ))}
        </ul>
      </Band>

      {/* ── Where it stands ────────────────────────────────────────────
          The honesty section, and the reason it is a `<dl>` rather than
          prose: a reader checking whether a claim is finished wants to scan
          for the one they care about, not read a paragraph that buries it.
          Its wording tracks the Trust Center's — the two must never diverge
          on a compliance status. */}
      <Band as="section" tone="page" space="lg" aria-labelledby="state-heading">
        <Container width="prose">
          <SectionHeader id="state-heading" align="start" eyebrow={t('about.state.eyebrow')}>
            {t('about.state.heading')}
          </SectionHeader>
          <p className="mt-6 text-[1.02rem] leading-[1.7] text-[color:var(--bd-body)]">
            {t('about.state.body')}
          </p>
          <dl className="mt-8 flex flex-col gap-5">
            {STATE_ROWS.map((id) => (
              <div key={id} className="border-s-2 border-[color:var(--bd-line)] ps-5">
                <dt className="font-mono rtl:font-sans text-[11px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
                  {t(`about.state.${id}.term`)}
                </dt>
                <dd className="mt-2 text-[0.98rem] leading-[1.66] text-[color:var(--bd-body)]">
                  {t(`about.state.${id}.desc`)}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-8 text-[0.96rem] leading-[1.66] text-[color:var(--bd-muted)]">
            {t('about.state.trustBefore')}{' '}
            <Link className={INLINE_LINK} to="/trust">
              {t('about.state.trustLink')}
            </Link>
            {t('about.state.trustAfter')}
          </p>
        </Container>
      </Band>

      {/* ── Closing ────────────────────────────────────────────────────── */}
      <Band as="section" tone="band" space="md" aria-label={t('about.closing.label')}>
        <Container width="prose" className="text-center">
          <Medallion icon={Compass} hue="cyan" onDark className="mx-auto mb-5" />
          <SectionTitle onDark className="mx-auto max-w-2xl text-[clamp(1.5rem,2.6vw,2rem)]">
            {t('about.closing.heading')}
          </SectionTitle>
          <Lead onDark className="mx-auto mt-4 max-w-xl">
            {t('about.closing.body')}
          </Lead>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/contact" className={ctaClasses({ onDark: true })}>
              {t('about.closing.primary')}
              <CtaArrow />
            </Link>
            <Link to="/how-it-works" className={ctaClasses({ variant: 'secondary', onDark: true })}>
              {t('about.closing.secondary')}
            </Link>
          </div>
        </Container>
      </Band>
    </main>
  );
}
